// Entity-agnostic column-schema framework. Each entity defines a
// `<entity>Schema.ts` exporting a `ColumnSchema[]`; the frontend grid and
// backend CSV export both derive from that array via the adapters below.

import type { ColumnConfig, ColumnResponsiveOverrides } from '../types/baseExplorerConfig.js';

export interface ColumnSchema {
  field: string;
  title: string;
  /** Column name OR SQL expression (e.g. `ST_Y(location)`, `caseSqlFromMap(...)`). */
  dbCol: string;
  /** SQL expression for ORDER BY when different from dbCol (e.g. JSON-shape columns). Falls back to dbCol then field. */
  sortExpr?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  filterField?: string;
  hiddenInGrid?: boolean;
  excludeFromGrid?: boolean;
  excludeFromExport?: boolean;
  order?: number;
  desktop?: ColumnResponsiveOverrides;
  mobile?: ColumnResponsiveOverrides;
}

export interface ExportColumn {
  db: string;
  header: string;
}

export function toGridColumn(c: ColumnSchema): ColumnConfig {
  return {
    field: c.field,
    title: c.title,
    width: c.width,
    minWidth: c.minWidth,
    maxWidth: c.maxWidth,
    resizable: c.resizable ?? true,
    sortable: c.sortable ?? true,
    filterable: c.filterable ?? false,
    filterField: c.filterField,
    hidden: c.hiddenInGrid ?? false,
    order: c.order,
    desktop: c.desktop,
    mobile: c.mobile,
  };
}

export function toExportColumn(c: ColumnSchema): ExportColumn {
  return { db: c.dbCol, header: c.title };
}

export const gridColumns = (schema: ColumnSchema[]): ColumnConfig[] =>
  schema.filter((c) => !c.excludeFromGrid).map(toGridColumn);

export const exportColumns = (schema: ColumnSchema[]): ExportColumn[] =>
  schema.filter((c) => !c.excludeFromExport).map(toExportColumn);

const SAFE_IDENT = /^[a-z_][a-z0-9_]*$/i;

function sortableExprOf(c: ColumnSchema): string | null {
  if (c.sortable === false) return null;
  if (c.sortExpr) return c.sortExpr;
  if (c.dbCol) return c.dbCol;
  return SAFE_IDENT.test(c.field) ? c.field : null;
}

/** Field names a service should accept as `sortBy`, derived from the schema. */
export const allowedSortFields = (schema: ColumnSchema[]): string[] =>
  schema.filter((c) => sortableExprOf(c) !== null).map((c) => c.field);

/** SQL expression to put in `ORDER BY` for the given field, or null if not sortable. */
export function sortExprFor(schema: ColumnSchema[], field: string | undefined): string | null {
  if (!field) return null;
  const c = schema.find((s) => s.field === field);
  return c ? sortableExprOf(c) : null;
}
